// src/services/billAttachmentService.js
// D-291 P3d-5b-4 - COPIES OF THE VENDOR'S BILL (photo / PDF) on a purchase bill.
//
// WHY THIS FILE EXISTS
// The bill entered in Zeno must be checkable against the paper. A copy is
// uploaded to Storage under companies/{company}/purchase_bills/{bill}/ and ONE
// entry is appended to the bill's attachments list. Only the Storage PATH is
// kept - never the download link - so every Open asks Storage again and
// storage.rules decide (same company, bill-entry roles). Copies are never
// deleted or replaced (a wrong one stays; the right one is added).
// Rules: firestore.rules attachOneCopy() + storage.rules purchase_bills path.
// Tests: tests/rules/d291 (P13, P14, N38-N47), tests/rules/storage (P5, N11, N12).
// Kept apart from purchaseBillService.js so that file (and its Jest mocks)
// never import Firebase Storage.

import { doc, getDoc, runTransaction, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { canEnterBills } from "./purchaseBillService";

export const MAX_BILL_COPIES = 10;
export const MAX_COPY_MB = 20;

const userName = (user) => user?.name || user?.displayName || user?.email || "Unknown";
// Letters, digits, dot, dash, underscore only - the file name stays one plain path segment.
const safeFileName = (name) => String(name || "bill").replace(/[^a-zA-Z0-9._-]/g, "_").slice(-80) || "bill";

/** Why this file cannot be attached, or null when it can. Same limits as storage.rules. */
export function checkBillCopy(file) {
  if (!file) return "Choose a photo or PDF of the bill.";
  const type = String(file.type || "");
  if (!(type.startsWith("image/") || type === "application/pdf")) return "Only a photo (JPG / PNG) or a PDF can be attached.";
  if (!(file.size > 0)) return "This file is empty.";
  if (file.size >= MAX_COPY_MB * 1024 * 1024) return `The file is too big - at most ${MAX_COPY_MB} MB.`;
  return null;
}

/**
 * Attach ONE copy: upload to Storage, then append its entry to the bill in
 * ONE transaction (the bill is re-read there - company, the 10-copy limit).
 * If the append is refused after the upload, the uploaded file stays unused
 * (storage.rules allow no delete) and nothing on the bill changes.
 */
export async function attachBillCopy({ company_id, bill_id, file, user } = {}) {
  const companyId = String(company_id || "").trim();
  const billId = String(bill_id || "").trim();
  if (!companyId) throw new Error("company_id required");
  if (!billId) throw new Error("Bill missing");
  if (!user?.uid) throw new Error("Your login could not be read. Please log in again.");
  if (!canEnterBills(user)) throw new Error("Only Owner, Accounts, SM, Manager or Store can attach a bill copy.");
  const why = checkBillCopy(file);
  if (why) throw new Error(why);

  const billRef = doc(db, "purchase_bills", billId);

  // Quick check BEFORE uploading, so a full or wrong bill uploads nothing.
  const pre = await getDoc(billRef);
  if (!pre.exists()) throw new Error("Bill not found");
  if (pre.data().company_id !== companyId) throw new Error("Access denied - bill of another company");
  const preCount = Array.isArray(pre.data().attachments) ? pre.data().attachments.length : 0;
  if (preCount >= MAX_BILL_COPIES) throw new Error(`This bill already has ${MAX_BILL_COPIES} copies - no more can be attached.`);

  const path = `companies/${companyId}/purchase_bills/${billId}/${Date.now()}_${safeFileName(file.name)}`;
  await uploadBytes(ref(storage, path), file, { contentType: file.type });

  // Exactly the keys firestore.rules attachOneCopy() allows - no download link.
  const entry = {
    path,
    name: String(file.name || "bill").slice(0, 120),
    size: file.size,
    content_type: file.type,
    uploaded_by: user.uid,
    uploaded_by_name: userName(user),
    uploaded_at: Timestamp.now(),
  };

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(billRef);
      if (!snap.exists()) throw new Error("Bill not found");
      const bill = snap.data();
      if (bill.company_id !== companyId) throw new Error("Access denied - bill of another company");
      const old = Array.isArray(bill.attachments) ? bill.attachments : [];
      if (old.length >= MAX_BILL_COPIES) throw new Error(`This bill already has ${MAX_BILL_COPIES} copies - no more can be attached.`);
      tx.update(billRef, { attachments: [...old, entry] });
    });
  } catch (e) {
    throw new Error(`The file was uploaded, but it could not be added to the bill - nothing changed on the bill. Try again.\n(${e?.message || "unknown error"})`);
  }
  return entry;
}

/** A fresh download link for one copy - storage.rules decide who may open it. */
export async function openBillCopy(path) {
  if (!path) throw new Error("Copy missing");
  return getDownloadURL(ref(storage, path));
}